import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import PortfolioSummaryCards from "@/components/PortfolioSummaryCards";
import AssetAllocationChart from "@/components/AssetAllocationChart";
import HoldingsTable from "@/components/HoldingsTable";
import TodaysTrades from "@/components/TodaysTrades";
import InvestmentTransactionList from "@/components/InvestmentTransactionList";
import InvestmentTransactionDialog from "@/components/InvestmentTransactionDialog";
import WhatIfCalculator from "@/components/WhatIfCalculator";
import ScenarioAlertsBanner from "@/components/ScenarioAlertsBanner";
import PerformanceChart from "@/components/PerformanceChart";
import RealizationsCard from "@/components/RealizationsCard";
import DividendsCard from "@/components/DividendsCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Investments() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWhatIfOpen, setIsWhatIfOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not authenticated');
      setUser(await res.json());
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchAssetClasses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/asset-classes`, { credentials: 'include' });
      if (res.ok) setAssetClasses(await res.json());
    } catch (err) {
      console.error('fetchAssetClasses failed:', err);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/investments/summary?currency=${currency}`, { credentials: 'include' });
      if (res.ok) setSummary(await res.json());
    } catch (err) {
      console.error('fetchSummary failed:', err);
    }
  }, [currency]);

  const fetchHoldings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/investments/holdings?currency=${currency}`, { credentials: 'include' });
      if (res.ok) setHoldings(await res.json());
    } catch (err) {
      console.error('fetchHoldings failed:', err);
    }
  }, [currency]);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/investments/transactions?currency=${currency}`, { credentials: 'include' });
      if (res.ok) setTransactions(await res.json());
    } catch (err) {
      console.error('fetchTransactions failed:', err);
    }
  }, [currency]);

  const refreshAll = useCallback(() => {
    fetchSummary();
    fetchHoldings();
    fetchTransactions();
    setRefreshKey(k => k + 1);
  }, [fetchSummary, fetchHoldings, fetchTransactions]);

  useEffect(() => {
    fetchUser();
    fetchAssetClasses();
  }, [fetchUser, fetchAssetClasses]);

  useEffect(() => {
    if (user) refreshAll();
  }, [user, currency, refreshAll]);

  const handleRefreshPrices = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/investments/refresh-prices?currency=${currency}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Prices refreshed');
        refreshAll();
      } else {
        toast.error('Failed to refresh prices');
      }
    } catch {
      toast.error('Failed to refresh prices');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      const res = await fetch(`${API}/investments/transactions/${transactionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Transaction deleted');
        refreshAll();
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const todaysTrades = transactions.filter(t => {
    const today = new Date().toISOString().substring(0, 10);
    return t.date === today;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} currency={currency} onCurrencyChange={setCurrency} onUserUpdate={setUser} />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold">Portfolio</h2>
            <p className="text-sm text-muted-foreground">
              Track your investments and P&amp;L across asset classes
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid="open-whatif-button"
              variant="outline"
              onClick={() => setIsWhatIfOpen(true)}
              className="transition-transform duration-200 hover:-translate-y-px"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              What-If
            </Button>
            <Button
              data-testid="refresh-prices-button"
              variant="outline"
              onClick={handleRefreshPrices}
              disabled={refreshing}
              className="transition-transform duration-200 hover:-translate-y-px"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Prices
            </Button>
            <Button
              data-testid="add-investment-button"
              onClick={() => setIsDialogOpen(true)}
              className="transition-transform duration-200 hover:-translate-y-px"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Transaction
            </Button>
          </div>
        </div>

        <ScenarioAlertsBanner
          currency={currency}
          onViewScenario={() => setIsWhatIfOpen(true)}
        />

        <PortfolioSummaryCards summary={summary} currency={currency} />

        <div className="grid gap-6 lg:grid-cols-2 mb-8">
          <AssetAllocationChart summary={summary} currency={currency} />
          <TodaysTrades trades={todaysTrades} assetClasses={assetClasses} currency={currency} />
        </div>

        <HoldingsTable
          holdings={holdings}
          assetClasses={assetClasses}
          currency={currency}
          onPriceUpdated={refreshAll}
        />

        <PerformanceChart currency={currency} />

        <RealizationsCard currency={currency} refreshKey={refreshKey} />

        <DividendsCard
          currency={currency}
          assetClasses={assetClasses}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey(k => k + 1)}
        />

        <InvestmentTransactionList
          transactions={transactions}
          assetClasses={assetClasses}
          currency={currency}
          onDelete={handleDeleteTransaction}
        />
      </main>

      <InvestmentTransactionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        assetClasses={assetClasses}
        currency={currency}
        holdings={holdings}
        onSaved={() => { setIsDialogOpen(false); refreshAll(); }}
      />

      <WhatIfCalculator
        open={isWhatIfOpen}
        onOpenChange={setIsWhatIfOpen}
        assetClasses={assetClasses}
        holdings={holdings}
        currency={currency}
      />
    </div>
  );
}

export default Investments;
